import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import {
  InvoiceImportFileSummary,
  InvoiceImportInfoReason,
  InvoiceImportLine,
  InvoiceImportReviewSummary,
  InvoiceImportReviewThresholds,
  InvoiceImportSubAdvice,
  InvoiceImportSubAdviceDuration,
  InvoiceImportSubAdviceEntry,
  WholesalerSubstitutionStrategy,
  InvoiceImportCreditKind,
  InvoiceImportSummary,
  SuggestedSalePriceRule,
} from 'src/app/models/invoice-import.model';
import { InvoiceCostLayersDialogComponent } from '../invoice-cost-layers-dialog/invoice-cost-layers-dialog.component';
import { InvoiceDraftProductDialogComponent } from '../invoice-draft-product-dialog/invoice-draft-product-dialog.component';
import {
  SubstitutionMappingDialogComponent,
  SubstitutionMappingDialogResult,
} from '../substitution-mapping-dialog/substitution-mapping-dialog.component';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { InvoiceImportService } from 'src/app/services/invoice-import.service';
import {
  applyBlockedReasonKey,
  attentionReasonKey,
  attentionReasonParams,
  buildInvoiceImportDisplayRows,
  countDeviation,
  countSourceKey,
  draftSkipReasonKey,
  formatDkk,
  goodsReceiptStatusKey,
  hasQtyPriceSuspect,
  InvoiceImportDisplayRow,
  isDraftLinkedLine,
  pairingStatusKey,
  formatKrPlain,
  formatPercent,
  formatSignedKrDelta,
  apiErrorCode,
  substitutionErrorMessage,
  isInvoiceImportPdfFile,
  isUnmatchedProductLine,
  sortInvoiceImportReviewLines,
  stockSkippedReasonKey,
  translateInstant,
  reviewStatusKey,
  lineTypeKey,
  matchStatusKey,
  infoReasonKey,
  resolveLineInfoReasons,
  visibleAttentionReasons,
} from 'src/app/services/invoice-import.util';

type LineReviewFilter = 'needs_review' | 'large' | 'toCheck' | 'unchanged' | 'autoApproved' | 'all';

interface LineDraft {
  acceptCostPrice: boolean;
  newSalePrice: number | null;
  costKeepReason: string;
}

@Component({
  selector: 'app-invoice-import-detail',
  templateUrl: './invoice-import-detail.component.html',
  styleUrls: ['./invoice-import-detail.component.scss'],
})
export class InvoiceImportDetailComponent implements OnInit, OnDestroy {
  @Input() storeId!: string;
  @Input() importId!: string;
  @Output() closed = new EventEmitter<boolean>();

  imp: InvoiceImportSummary | null = null;
  pairedImport: InvoiceImportSummary | null = null;
  lines: InvoiceImportLine[] = [];
  storeCategories: { _id: string; name: string }[] = [];
  pairPickerOpen = false;
  pairSearchQ = '';
  pairSearchResults: InvoiceImportSummary[] = [];
  canApply = false;
  applyBlockedReasons: string[] = [];
  applyWarnings: string[] = [];
  reviewSummary: InvoiceImportReviewSummary | null = null;
  thresholds: InvoiceImportReviewThresholds | null = null;
  loadError = '';
  lineFilter: LineReviewFilter = 'needs_review';
  autoApprovedPanelOpen = false;

  lineDrafts: Record<string, LineDraft> = {};
  lineApproveErrors: Record<string, string> = {};
  productSearchQ = '';
  productSearchResults: any[] = [];
  productSearchLineId: string | null = null;
  private pdfObjectUrl: string | null = null;
  private pdfRevokeTimer: ReturnType<typeof setTimeout> | null = null;

  applySummary: any = null;
  highlightLineId: string | null = null;

  readonly costKeepQuickPickKeys = ['CAMPAIGN', 'WRONG_INVOICE', 'OTHER'] as const;

  constructor(
    private invoiceImportService: InvoiceImportService,
    private ecommerceService: EcommerceService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadStoreCategories();
    this.loadDetail();
  }

  private loadStoreCategories(): void {
    this.ecommerceService.getStoreCategories(this.storeId, { adminView: 'true' }).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.storeCategories = data.categories || data || [];
      },
      error: () => {
        this.storeCategories = [];
      },
    });
  }

  ngOnDestroy(): void {
    this.schedulePdfRevoke(0);
  }

  loadDetail(): void {
    this.loadError = '';
    this.spinner.show();
    this.invoiceImportService.getImportDetail(this.storeId, this.importId).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.imp = data.import;
        this.pairedImport = data.pairedImport ?? null;
        this.lines = data.lines || [];
        this.canApply = !!data.canApply;
        this.applyBlockedReasons = data.applyBlockedReasons || [];
        this.applyWarnings = data.applyWarnings || [];
        this.reviewSummary = data.reviewSummary || null;
        this.thresholds = data.thresholds || null;
        this.syncDrafts();
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.loadError = err.error?.response?.message || err.message;
      },
    });
  }

  syncDrafts(): void {
    const next: Record<string, LineDraft> = {};
    for (const line of this.lines) {
      next[line._id] = {
        acceptCostPrice: line.acceptCostPrice !== false,
        newSalePrice: line.newSalePrice ?? null,
        costKeepReason: line.costKeepReason || '',
      };
    }
    this.lineDrafts = next;
  }

  setLineFilter(filter: LineReviewFilter): void {
    this.lineFilter = filter;
    if (filter === 'autoApproved') {
      this.autoApprovedPanelOpen = true;
    } else {
      this.autoApprovedPanelOpen = false;
    }
  }

  get autoApprovedLines(): InvoiceImportLine[] {
    return sortInvoiceImportReviewLines(
      this.lines.filter((l) => l.lineType === 'product' && l.reviewStatus === 'auto_approved')
    );
  }

  get isCreditNote(): boolean {
    return this.imp?.documentType === 'credit_note';
  }

  get displayRows(): InvoiceImportDisplayRow[] {
    return buildInvoiceImportDisplayRows(
      this.visibleLines,
      this.lines,
      this.lineFilter === 'all'
    );
  }

  get draftableUnmatchedCount(): number {
    return this.lines.filter(
      (l) =>
        l.lineType === 'product' &&
        l.matchStatus === 'unmatched' &&
        l.reviewStatus !== 'imported' &&
        l.reviewStatus !== 'applying'
    ).length;
  }

  get showPairedArchiveBanner(): boolean {
    return this.imp?.status === 'paired' && this.imp?.pairing?.role === 'archive';
  }

  get visibleLines(): InvoiceImportLine[] {
    let pool = this.lines.filter((l) => l.lineType === 'product');
    switch (this.lineFilter) {
      case 'large':
        pool = pool.filter((l) => this.isLargeReviewLine(l));
        break;
      case 'toCheck':
        pool = pool.filter((l) => this.isToCheckReviewLine(l));
        break;
      case 'autoApproved':
        pool = pool.filter((l) => l.reviewStatus === 'auto_approved');
        break;
      case 'unchanged':
        pool = pool.filter((l) => this.isUnchangedPendingLine(l));
        break;
      case 'all':
        break;
      case 'needs_review':
      default:
        pool = pool.filter(
          (l) => l.reviewStatus !== 'auto_approved' && (this.isLargeReviewLine(l) || this.isToCheckReviewLine(l))
        );
        break;
    }
    if (this.lineFilter === 'all') {
      return sortInvoiceImportReviewLines(pool);
    }
    return sortInvoiceImportReviewLines(pool);
  }

  isLargeReviewLine(line: InvoiceImportLine): boolean {
    return line.reviewStatus === 'pending' && line.changeSeverity === 'large';
  }

  isToCheckReviewLine(line: InvoiceImportLine): boolean {
    if (line.reviewStatus !== 'pending') {
      return false;
    }
    if (line.changeSeverity === 'large') {
      return false;
    }
    return !!(line.needsAttention || line.matchStatus === 'unmatched' || line.matchStatus === 'ambiguous');
  }

  isUnchangedPendingLine(line: InvoiceImportLine): boolean {
    return (
      line.reviewStatus === 'pending' &&
      line.matchStatus === 'matched' &&
      !line.needsAttention
    );
  }

  get unchangedPendingCount(): number {
    return this.reviewSummary?.unchangedPending ?? 0;
  }

  get showUnchangedPendingHint(): boolean {
    return this.lineFilter === 'needs_review' && this.unchangedPendingCount > 0;
  }

  get subAdviceBlock(): InvoiceImportSubAdvice | null {
    const raw = this.imp?.parsedStoreIdentifiers?.['subAdvice'];
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const block = raw as InvoiceImportSubAdvice;
    return block.advice?.length ? block : null;
  }

  sourceFormatBadgeLabel(): string {
    if (this.imp?.sourceFormat === 'fak') {
      return this.translate.instant('INVOICE_IMPORTS.DETAIL.SOURCE_FAK');
    }
    return this.translate.instant('INVOICE_IMPORTS.DETAIL.SOURCE_PDF');
  }

  isPdfFile(file: InvoiceImportFileSummary): boolean {
    return isInvoiceImportPdfFile(file);
  }

  showPrimaryPdfOpen(): boolean {
    const files = this.imp?.files || [];
    return files.some((f) => this.isPdfFile(f));
  }

  subAdviceEntryLabel(entry: InvoiceImportSubAdviceEntry): string {
    if (entry.status === 'sold_out') {
      return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.SUB_SOLD_OUT', {
        name: entry.name || '—',
        quantity: entry.quantity ?? '—',
      });
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.SUB_SUBSTITUTION', {
      name: entry.name || '—',
      replacement: entry.replacementName || '—',
      quantity: entry.quantity ?? '—',
    });
  }

  isLegacySubAdvice(sub: InvoiceImportSubAdvice): boolean {
    if (sub.legacy) {
      return true;
    }
    if (sub.parserVersion === 2) {
      return false;
    }
    return sub.advice.some((e) => e.status === 'sold_out' || e.status === 'substitution');
  }

  subStatusTextDisplay(entry: InvoiceImportSubAdviceEntry): string {
    const text = (entry.statusText || '').trim();
    return text || '—';
  }

  subStatusIsEmptyShort(entry: InvoiceImportSubAdviceEntry): boolean {
    return entry.lineFormat === 'short' && !(entry.statusText || '').trim();
  }

  subDurationBadgeClass(duration?: InvoiceImportSubAdviceDuration | null): string {
    if (duration === 'permanent') {
      return 'bg-dark';
    }
    if (duration === 'temporary') {
      return 'bg-warning text-dark';
    }
    return 'bg-secondary';
  }

  subDurationLabel(duration?: InvoiceImportSubAdviceDuration | null): string {
    const key =
      duration === 'permanent'
        ? 'INVOICE_IMPORTS.SUB.DURATION_PERMANENT'
        : duration === 'temporary'
          ? 'INVOICE_IMPORTS.SUB.DURATION_TEMPORARY'
          : 'INVOICE_IMPORTS.SUB.DURATION_UNKNOWN';
    return this.translate.instant(key);
  }

  subNoticeKindLabel(kind?: string | null): string {
    if (!kind) {
      return '';
    }
    const key = `INVOICE_IMPORTS.SUB.KIND.${kind.toUpperCase()}`;
    const v = this.translate.instant(key);
    return v === key ? kind : v;
  }

  substitutionStrategyLabel(strategy: WholesalerSubstitutionStrategy): string {
    return this.translate.instant(`INVOICE_IMPORTS.SUB.STRATEGY_${strategy.toUpperCase()}`);
  }

  subNoticeTooltip(line: InvoiceImportLine): string {
    const sn = line.subNotice;
    if (!sn) {
      return '';
    }
    const roleKey =
      sn.role === 'from' ? 'INVOICE_IMPORTS.SUB.ROLE_FROM' : 'INVOICE_IMPORTS.SUB.ROLE_TO';
    return `${this.translate.instant(roleKey)} · ${this.subNoticeKindLabel(sn.noticeKind)}`;
  }

  scrollToSubAdviceRow(adviceIndex: number): void {
    const el = document.getElementById(`sub-advice-row-${adviceIndex}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.classList.add('sub-row-highlight');
    window.setTimeout(() => el?.classList.remove('sub-row-highlight'), 2500);
  }

  scrollToInvoiceLine(lineId: string): void {
    this.highlightLineId = lineId;
    const el = document.getElementById(`invoice-line-${lineId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      if (this.highlightLineId === lineId) {
        this.highlightLineId = null;
      }
    }, 2500);
  }

  openSaveSubMapping(entry: InvoiceImportSubAdviceEntry): void {
    if (entry.index == null) {
      return;
    }
    this.dialogService
      .open(SubstitutionMappingDialogComponent, {
        context: { entry },
        closeOnBackdropClick: false,
      })
      .onClose.subscribe((payload: SubstitutionMappingDialogResult | null) => {
        if (!payload) {
          return;
        }
        const mappingId = entry.mapping?._id;
        if (mappingId) {
          this.persistSubstitutionPatch(mappingId, payload, () => this.loadDetail());
        } else {
          this.persistSubstitutionCreate(entry, payload, () => this.loadDetail());
        }
      });
  }

  private persistSubstitutionCreate(
    entry: InvoiceImportSubAdviceEntry,
    payload: SubstitutionMappingDialogResult,
    onSuccess: () => void
  ): void {
    this.spinner.show();
    const body: Record<string, unknown> = {
      supplierKey: this.imp?.supplierKey || 'DAGROFA',
      source: 'sub_advice',
      importId: this.importId,
      adviceIndex: entry.index,
      strategy: payload.strategy,
    };
    if (payload.toSupplierItemNumber) {
      body['toSupplierItemNumber'] = payload.toSupplierItemNumber;
    }
    if (payload.toName) {
      body['toName'] = payload.toName;
    }
    if (payload.confirmed) {
      body['confirmed'] = true;
    }
    this.invoiceImportService.createSubstitution(this.storeId, body).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.SUB.SAVE_OK'));
        onSuccess();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.handleSubstitutionCreateError(err, payload);
      },
    });
  }

  private substitutionPatchBody(payload: SubstitutionMappingDialogResult): Record<string, unknown> {
    const body: Record<string, unknown> = { strategy: payload.strategy };
    if (payload.toSupplierItemNumber !== undefined) {
      body['toSupplierItemNumber'] = payload.toSupplierItemNumber;
    }
    if (payload.toName !== undefined) {
      body['toName'] = payload.toName;
    }
    return body;
  }

  private persistSubstitutionPatch(
    substitutionId: string,
    payload: SubstitutionMappingDialogResult,
    onSuccess: () => void
  ): void {
    this.spinner.show();
    this.invoiceImportService
      .patchSubstitution(this.storeId, substitutionId, this.substitutionPatchBody(payload))
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.toastr.success(this.translate.instant('INVOICE_IMPORTS.SUB.SAVE_OK'));
          onSuccess();
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', substitutionErrorMessage(this.translate, err), 'error');
        },
      });
  }

  private handleSubstitutionCreateError(err: any, payload: SubstitutionMappingDialogResult): void {
    const code = apiErrorCode(err);
    const data = err.error?.response?.data || err.error?.data || {};
    const existingId = data.existingId as string | undefined;
    if (code === 'SUBSTITUTION_EXISTS' && existingId) {
      Swal.fire({
        title: this.translate.instant('INVOICE_IMPORTS.SUB.EXISTS_TITLE'),
        text: this.translate.instant('INVOICE_IMPORTS.SUB.EXISTS_BODY'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.translate.instant('INVOICE_IMPORTS.SUB.EXISTS_EDIT'),
        cancelButtonText: this.translate.instant('INVOICE_IMPORTS.COMMON.CANCEL'),
      }).then((r) => {
        if (r.isConfirmed) {
          this.persistSubstitutionPatch(existingId, payload, () => this.loadDetail());
        }
      });
      return;
    }
    Swal.fire('Error', substitutionErrorMessage(this.translate, err), 'error');
  }

  get hasReconciliationWarning(): boolean {
    return (
      this.applyWarnings.includes('reconciliation_mismatch') ||
      this.imp?.reconciliation?.status === 'mismatch'
    );
  }

  get applyLineCount(): number {
    return this.lines.filter(
      (l) =>
        l.lineType === 'product' &&
        (l.reviewStatus === 'approved' || l.reviewStatus === 'auto_approved' || l.reviewStatus === 'auto_marked')
    ).length;
  }

  get showNoLinesState(): boolean {
    return this.lines.length === 0;
  }

  get showEmptyFilterState(): boolean {
    return !this.showNoLinesState && this.displayRows.length === 0 && !this.autoApprovedPanelOpen;
  }

  back(refresh = false): void {
    this.closed.emit(refresh);
  }

  formatMoney = formatDkk;
  formatPct = formatPercent;
  formatKrPlain = formatKrPlain;
  formatPercent = formatPercent;
  visibleReasons = visibleAttentionReasons;

  attentionLabel(line: InvoiceImportLine, reason: string): string {
    const key = attentionReasonKey(reason as any);
    return translateInstant(this.translate, key, attentionReasonParams(reason as any, line));
  }

  lineInfoReasons(line: InvoiceImportLine): InvoiceImportInfoReason[] {
    return resolveLineInfoReasons(line);
  }

  infoReasonLabel(reason: InvoiceImportInfoReason): string {
    return translateInstant(this.translate, infoReasonKey(reason));
  }

  showUseRecommendedPrice(line: InvoiceImportLine): boolean {
    const rec = line.recommendedRetailInclVat;
    if (rec == null || Number.isNaN(rec)) {
      return false;
    }
    if (line.suggestedSalePriceRule === 'recommended_retail') {
      return false;
    }
    const current = line.productSnapshot?.price;
    if (current == null || Number.isNaN(current)) {
      return true;
    }
    return Math.abs(rec - current) > 0.005;
  }

  showRecommendedOnly(line: InvoiceImportLine): boolean {
    return line.productSnapshot == null && line.recommendedRetailInclVat != null;
  }

  recommendedOnlyLabel(line: InvoiceImportLine): string {
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.RECOMMENDED_ONLY', {
      rec: formatKrPlain(line.recommendedRetailInclVat!),
    });
  }

  newCostOnlyLabel(line: InvoiceImportLine): string {
    const cost = line.newCostExclVat ?? line.netUnitCostExclVat;
    if (cost == null) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.NEW_COST_ONLY', {
      cost: formatKrPlain(cost),
    });
  }

  blockedReasonLabel(code: string): string {
    return translateInstant(this.translate, applyBlockedReasonKey(code));
  }

  stockSkippedLabel(code: string | undefined): string {
    if (!code) {
      return '';
    }
    return translateInstant(this.translate, stockSkippedReasonKey(code));
  }

  costSkippedLabel(code: string | undefined): string {
    if (!code) {
      return '';
    }
    return translateInstant(this.translate, `INVOICE_IMPORTS.COST_SKIPPED.${code.toUpperCase()}`);
  }

  openPdf(): void {
    const file = (this.imp?.files || []).find((f) => this.isPdfFile(f));
    if (file) {
      this.openFileInline(file);
    }
  }

  openFileInline(file: InvoiceImportFileSummary): void {
    if (!this.isPdfFile(file)) {
      this.downloadImportFile(file);
      return;
    }
    const pdfWindow = window.open('', '_blank');
    if (!pdfWindow) {
      Swal.fire(
        this.translate.instant('INVOICE_IMPORTS.DETAIL.PDF_POPUP_BLOCKED_TITLE'),
        this.translate.instant('INVOICE_IMPORTS.DETAIL.PDF_POPUP_BLOCKED'),
        'warning'
      );
      return;
    }
    this.spinner.show();
    this.invoiceImportService.getImportFileBlob(this.storeId, this.importId, file._id).subscribe({
      next: (blob) => {
        this.spinner.hide();
        this.pdfObjectUrl = URL.createObjectURL(blob);
        pdfWindow.location.href = this.pdfObjectUrl;
        this.schedulePdfRevoke(120_000);
      },
      error: (err: any) => {
        this.spinner.hide();
        pdfWindow.close();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  downloadImportFile(file: InvoiceImportFileSummary): void {
    this.spinner.show();
    this.invoiceImportService.getImportFileBlob(this.storeId, this.importId, file._id).subscribe({
      next: (blob) => {
        this.spinner.hide();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = file.filename || 'invoice-file';
        anchor.rel = 'noopener';
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  onImportFileAction(file: InvoiceImportFileSummary): void {
    if (this.isPdfFile(file)) {
      this.openFileInline(file);
    } else {
      this.downloadImportFile(file);
    }
  }

  importFileActionLabel(file: InvoiceImportFileSummary): string {
    if (this.isPdfFile(file)) {
      return this.translate.instant('INVOICE_IMPORTS.DETAIL.FILE_OPEN');
    }
    return this.translate.instant('INVOICE_IMPORTS.DETAIL.FILE_DOWNLOAD');
  }

  private schedulePdfRevoke(delayMs: number): void {
    if (this.pdfRevokeTimer) {
      clearTimeout(this.pdfRevokeTimer);
      this.pdfRevokeTimer = null;
    }
    if (delayMs <= 0) {
      if (this.pdfObjectUrl) {
        URL.revokeObjectURL(this.pdfObjectUrl);
        this.pdfObjectUrl = null;
      }
      return;
    }
    this.pdfRevokeTimer = setTimeout(() => {
      if (this.pdfObjectUrl) {
        URL.revokeObjectURL(this.pdfObjectUrl);
        this.pdfObjectUrl = null;
      }
      this.pdfRevokeTimer = null;
    }, delayMs);
  }

  clearApplySummary(): void {
    this.applySummary = null;
  }

  reviewStatusLabel(status: string): string {
    return translateInstant(this.translate, reviewStatusKey(status));
  }

  severityLabel(severity: string | undefined): string {
    if (!severity || severity === 'none' || severity === 'small') {
      return '';
    }
    return translateInstant(this.translate, `INVOICE_IMPORTS.SEVERITY.${severity.toUpperCase()}`);
  }

  lineTypeLabel(lineType: string): string {
    return translateInstant(this.translate, lineTypeKey(lineType));
  }

  matchStatusLabel(matchStatus: string | undefined): string {
    if (!matchStatus) {
      return '';
    }
    return translateInstant(this.translate, matchStatusKey(matchStatus));
  }

  openImportById(importId: string): void {
    if (!importId || importId === this.importId) {
      return;
    }
    this.clearApplySummary();
    this.importId = importId;
    this.lineFilter = 'needs_review';
    this.autoApprovedPanelOpen = false;
    this.loadDetail();
  }

  failedLineLabel(lineId: string): string {
    const line = this.lines.find((l) => l._id === lineId);
    return line ? this.lineProductName(line) : lineId;
  }

  patchLineField(line: InvoiceImportLine, extra: Record<string, unknown> = {}): void {
    const draft = this.lineDrafts[line._id];
    if (!draft || line.reviewStatus === 'imported' || line.reviewStatus === 'applying') {
      return;
    }
    const body: Record<string, unknown> = {
      newSalePrice: draft.newSalePrice,
      ...extra,
    };
    if (!this.isCreditNote) {
      body['acceptCostPrice'] = draft.acceptCostPrice;
    }
    this.invoiceImportService.patchLine(this.storeId, this.importId, line._id, body).subscribe({
      next: (res: any) => {
        this.applyLineResponse(res);
      },
      error: (err: any) => {
        this.toastr.error(err.error?.response?.message || err.message);
      },
    });
  }

  onAcceptCostToggle(line: InvoiceImportLine): void {
    const draft = this.lineDrafts[line._id];
    if (draft?.acceptCostPrice) {
      draft.costKeepReason = '';
    }
    this.patchLineField(line);
  }

  applyCostKeepQuickPick(line: InvoiceImportLine, pickKey: string): void {
    const draft = this.lineDrafts[line._id];
    if (!draft) {
      return;
    }
    draft.costKeepReason = this.translate.instant(`INVOICE_IMPORTS.DETAIL.COST_KEEP_PICK.${pickKey}`);
  }

  costKeepQuickPickLabel(pickKey: string): string {
    return this.translate.instant(`INVOICE_IMPORTS.DETAIL.COST_KEEP_PICK.${pickKey}`);
  }

  useSuggestedPrice(line: InvoiceImportLine): void {
    const draft = this.lineDrafts[line._id];
    if (!draft || line.suggestedSalePrice == null) {
      return;
    }
    draft.newSalePrice = line.suggestedSalePrice;
    this.afterDraftSalePriceSet(line);
  }

  useRecommendedPrice(line: InvoiceImportLine): void {
    const draft = this.lineDrafts[line._id];
    if (!draft || line.recommendedRetailInclVat == null) {
      return;
    }
    draft.newSalePrice = line.recommendedRetailInclVat;
    this.afterDraftSalePriceSet(line);
  }

  private afterDraftSalePriceSet(line: InvoiceImportLine): void {
    if (isUnmatchedProductLine(line)) {
      this.patchLineField(line);
      this.startProductSearch(line);
      this.toastr.info(this.translate.instant('INVOICE_IMPORTS.DETAIL.LINK_TO_APPROVE'));
      return;
    }
    this.approveLine(line);
  }

  suggestedPriceChipLabel(line: InvoiceImportLine): string {
    const rule = line.suggestedSalePriceRule as SuggestedSalePriceRule | null | undefined;
    const price = line.suggestedSalePrice;
    if (!rule || price == null) {
      return '';
    }
    const min = this.thresholds?.minimumMarginPercent ?? 15;
    return translateInstant(this.translate, `INVOICE_IMPORTS.DETAIL.SUGGESTED_PRICE.${rule.toUpperCase()}`, {
      price: formatKrPlain(price),
      min,
    });
  }

  costChangeLabel(line: InvoiceImportLine): string {
    const oldCost = line.productSnapshot?.costPrice;
    const newCost = line.proposedCostPrice;
    if (oldCost == null || newCost == null) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.COST_CHANGE', {
      old: formatKrPlain(oldCost),
      new: formatKrPlain(newCost),
      deltaKr: formatSignedKrDelta(line.costDelta),
      deltaPct: formatPercent(line.costDeltaPercent),
    });
  }

  retailGapLabel(line: InvoiceImportLine): string {
    if (line.recommendedRetailInclVat == null || line.retailGap == null) {
      return '';
    }
    const key =
      line.retailGap < 0
        ? 'INVOICE_IMPORTS.DETAIL.RETAIL_GAP_BELOW'
        : line.retailGap > 0
          ? 'INVOICE_IMPORTS.DETAIL.RETAIL_GAP_ABOVE'
          : 'INVOICE_IMPORTS.DETAIL.RETAIL_GAP_EQUAL';
    return translateInstant(this.translate, key, {
      rec: formatKrPlain(line.recommendedRetailInclVat),
      deltaKr: formatSignedKrDelta(line.retailGap),
      deltaPct: formatPercent(line.retailGapPercent),
    });
  }

  marginAtCurrentLabel(line: InvoiceImportLine): string {
    const min = this.thresholds?.minimumMarginPercent ?? 15;
    const low = line.marginPercentAtCurrentPrice != null && line.marginPercentAtCurrentPrice < min;
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.MARGIN_AT_CURRENT', {
      value: formatPercent(line.marginPercentAtCurrentPrice, 1),
      low: low ? this.translate.instant('INVOICE_IMPORTS.DETAIL.MARGIN_LOW', { min }) : '',
    });
  }

  marginTransitionLabel(line: InvoiceImportLine): string {
    if (
      line.marginPercentAtCurrentPriceOldCost == null ||
      line.marginPercentAtCurrentPrice == null
    ) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.MARGIN_TRANSITION', {
      old: formatPercent(line.marginPercentAtCurrentPriceOldCost, 1),
      new: formatPercent(line.marginPercentAtCurrentPrice, 1),
    });
  }

  marginAtRecommendedLabel(line: InvoiceImportLine): string {
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.MARGIN_AT_RECOMMENDED', {
      value: formatPercent(line.marginPercentAtRecommendedPrice, 1),
    });
  }

  autoApprovalTooltip(line: InvoiceImportLine): string {
    const aa = line.autoApproval;
    if (!aa) {
      return '';
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.AUTO_APPROVAL_TOOLTIP', {
      rule: aa.rule,
      small: aa.thresholds.smallPercent,
      min: aa.thresholds.minimumMarginPercent,
      approvedAt: aa.approvedAt,
    });
  }

  toggleAutoApprovedPanel(): void {
    this.autoApprovedPanelOpen = !this.autoApprovedPanelOpen;
    if (this.autoApprovedPanelOpen) {
      this.lineFilter = 'autoApproved';
    }
  }

  undoAutoApprove(line: InvoiceImportLine): void {
    this.spinner.show();
    this.invoiceImportService.undoAutoApproveLine(this.storeId, this.importId, line._id).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.applyLineResponse(res);
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.DETAIL.UNDO_AUTO_APPROVE_DONE'));
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  approveLine(line: InvoiceImportLine): void {
    const draft = this.lineDrafts[line._id];
    this.lineApproveErrors[line._id] = '';
    const body: Record<string, unknown> = {
      newSalePrice: draft?.newSalePrice,
    };
    if (!this.isCreditNote) {
      body['acceptCostPrice'] = draft?.acceptCostPrice !== false;
      if (draft && !draft.acceptCostPrice) {
        body['costKeepReason'] = (draft.costKeepReason || '').trim();
      }
    }
    this.spinner.show();
    this.invoiceImportService.approveLine(this.storeId, this.importId, line._id, body).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.applyLineResponse(res);
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.DETAIL.LINE_APPROVED'));
      },
      error: (err: any) => {
        this.spinner.hide();
        const code = apiErrorCode(err);
        if (code === 'COST_KEEP_REASON_REQUIRED') {
          this.lineApproveErrors[line._id] = this.translate.instant('INVOICE_IMPORTS.DETAIL.COST_KEEP_REASON_REQUIRED');
          return;
        }
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  rejectLine(line: InvoiceImportLine): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.DETAIL.REJECT_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.DETAIL.REJECT_TOOLTIP'),
      input: 'textarea',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('INVOICE_IMPORTS.DETAIL.REJECT_CONFIRM'),
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.invoiceImportService
        .rejectLine(this.storeId, this.importId, line._id, result.value || undefined)
        .subscribe({
          next: (res: any) => {
            this.spinner.hide();
            this.applyLineResponse(res);
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', err.error?.response?.message || err.message, 'error');
          },
        });
    });
  }

  bulkApproveUnchanged(): void {
    this.clearApplySummary();
    this.spinner.show();
    this.invoiceImportService.bulkApproveUnchanged(this.storeId, this.importId).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        const data = res.response?.data || res.data || {};
        this.toastr.success(
          this.translate.instant('INVOICE_IMPORTS.DETAIL.BULK_APPROVED', { count: data.approved || 0 })
        );
        this.loadDetail();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  confirmApply(): void {
    const count = this.applyLineCount;
    const textKey = this.isCreditNote
      ? 'INVOICE_IMPORTS.PHASE2.APPLY_TEXT_CREDIT'
      : 'INVOICE_IMPORTS.DETAIL.APPLY_TEXT';
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.DETAIL.APPLY_TITLE'),
      text: this.translate.instant(textKey, { count }),
      icon: 'question',
      showCancelButton: true,
    }).then((r) => {
      if (r.isConfirmed) {
        this.runApply();
      }
    });
  }

  runApply(): void {
    this.clearApplySummary();
    this.spinner.show();
    this.invoiceImportService.applyImport(this.storeId, this.importId).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.applySummary = res.response?.data || res.data;
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.DETAIL.APPLY_DONE'));
        this.loadDetail();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  reparse(): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.DETAIL.REPARSE_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.DETAIL.REPARSE_TEXT'),
      icon: 'warning',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.clearApplySummary();
      this.spinner.show();
      this.invoiceImportService.reparse(this.storeId, this.importId).subscribe({
        next: () => {
          this.spinner.hide();
          this.loadDetail();
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err.error?.response?.message || err.message, 'error');
        },
      });
    });
  }

  archiveImport(): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.DETAIL.ARCHIVE_TITLE'),
      icon: 'question',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.invoiceImportService.archive(this.storeId, this.importId).subscribe({
        next: () => {
          this.toastr.success(this.translate.instant('INVOICE_IMPORTS.DETAIL.ARCHIVED'));
          this.back(true);
        },
        error: (err: any) => {
          Swal.fire('Error', err.error?.response?.message || err.message, 'error');
        },
      });
    });
  }

  hasImportedLine(): boolean {
    return this.lines.some((l) => l.reviewStatus === 'imported' || l.reviewStatus === 'applying');
  }

  isApprovableLine(line: InvoiceImportLine): boolean {
    return (
      line.lineType === 'product' &&
      line.reviewStatus !== 'imported' &&
      line.reviewStatus !== 'applying' &&
      line.reviewStatus !== 'auto_approved'
    );
  }

  rowShowsActions(row: InvoiceImportDisplayRow): boolean {
    return !row.hideApproveReject && this.isApprovableLine(row.line);
  }

  isDraftLinkedLine = isDraftLinkedLine;

  canCreateDraft(line: InvoiceImportLine): boolean {
    return (
      line.lineType === 'product' &&
      line.matchStatus === 'unmatched' &&
      line.reviewStatus !== 'imported' &&
      line.reviewStatus !== 'applying'
    );
  }

  openCreateDraftDialog(line: InvoiceImportLine): void {
    this.dialogService
      .open(InvoiceDraftProductDialogComponent, {
        context: { line, categories: this.storeCategories },
        closeOnBackdropClick: false,
      })
      .onClose.subscribe((payload) => {
        if (!payload) {
          return;
        }
        this.spinner.show();
        this.invoiceImportService
          .createLineDraftProduct(this.storeId, this.importId, line._id, payload)
          .subscribe({
            next: (res: any) => {
              this.spinner.hide();
              this.applyLineResponse(res);
              this.toastr.success(this.translate.instant('INVOICE_IMPORTS.PHASE2.DRAFT_CREATED'));
            },
            error: (err: any) => {
              this.spinner.hide();
              this.showApiError(err);
            },
          });
      });
  }

  bulkCreateDrafts(): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.PHASE2.BULK_DRAFT_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.PHASE2.BULK_DRAFT_TEXT', {
        count: this.draftableUnmatchedCount,
      }),
      icon: 'question',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.invoiceImportService.createDraftProductsBulk(this.storeId, this.importId, {}).subscribe({
        next: (res: any) => {
          this.spinner.hide();
          const data = res.response?.data || res.data || {};
          this.toastr.success(
            this.translate.instant('INVOICE_IMPORTS.PHASE2.BULK_DRAFT_DONE', { count: data.created || 0 })
          );
          if (data.skipped?.length) {
            const lines = data.skipped
              .map((s: { lineId: string; code: string }) => {
                const label = this.failedLineLabel(s.lineId);
                const reason = this.translate.instant(draftSkipReasonKey(s.code));
                return `${label}: ${reason}`;
              })
              .join('\n');
            Swal.fire(
              this.translate.instant('INVOICE_IMPORTS.PHASE2.BULK_DRAFT_SKIPPED_TITLE'),
              lines,
              'info'
            );
          }
          this.loadDetail();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.showApiError(err);
        },
      });
    });
  }

  openCostLayers(line: InvoiceImportLine): void {
    if (!line.productId) {
      return;
    }
    this.dialogService.open(InvoiceCostLayersDialogComponent, {
      context: {
        storeId: this.storeId,
        productId: line.productId,
        productName: this.lineProductName(line),
      },
      closeOnBackdropClick: true,
    });
  }

  setCreditKind(line: InvoiceImportLine, kind: InvoiceImportCreditKind): void {
    this.patchLineField(line, { creditKind: kind });
  }

  swapQtyPrice(line: InvoiceImportLine): void {
    this.spinner.show();
    this.invoiceImportService.patchLine(this.storeId, this.importId, line._id, { swapQtyPrice: true }).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.applyLineResponse(res);
      },
      error: (err: any) => {
        this.spinner.hide();
        this.showApiError(err);
      },
    });
  }

  hasQtyPriceSuspect = hasQtyPriceSuspect;

  countDeviationLabel(line: InvoiceImportLine): string {
    const dev = countDeviation(line);
    if (dev == null) {
      return '';
    }
    const sign = dev > 0 ? '+' : '';
    return `${sign}${dev}`;
  }

  countTooltip(line: InvoiceImportLine): string {
    if (line.countedUnits == null) {
      return '';
    }
    const parts: string[] = [];
    if (line.countSource) {
      parts.push(this.translate.instant(countSourceKey(line.countSource)));
    }
    if (line.countedAt) {
      parts.push(line.countedAt);
    }
    if (line.countDeviceId) {
      parts.push(line.countDeviceId);
    }
    return parts.join(' · ');
  }

  goodsReceiptBadgeLabel(): string {
    const status = this.imp?.goodsReceipt?.status || 'none';
    return this.translate.instant(goodsReceiptStatusKey(status));
  }

  pairingStatusLabel(): string {
    const status = this.imp?.pairing?.status || 'none';
    return this.translate.instant(pairingStatusKey(status));
  }

  openPrimaryPairedImport(): void {
    const id = this.imp?.pairing?.pairedImportId || this.pairedImport?._id;
    if (id) {
      this.openImportById(id);
    }
  }

  openLinkedPairedImport(): void {
    const id = this.pairedImport?._id || this.imp?.pairing?.pairedImportId;
    if (id && id !== this.importId) {
      this.openImportById(id);
    }
  }

  togglePairPicker(): void {
    this.pairPickerOpen = !this.pairPickerOpen;
    if (this.pairPickerOpen) {
      this.pairSearchQ = this.imp?.invoiceNumber || '';
      this.searchPairCandidates();
    }
  }

  searchPairCandidates(): void {
    const q = (this.pairSearchQ || '').trim();
    if (q.length < 2) {
      this.pairSearchResults = [];
      return;
    }
    this.invoiceImportService
      .listImports(this.storeId, { q, limit: 15, page: 1 })
      .subscribe({
        next: (res: any) => {
          const data = res.response?.data || res.data || {};
          const items: InvoiceImportSummary[] = data.items || [];
          const myFormat = this.imp?.sourceFormat;
          this.pairSearchResults = items.filter(
            (item) =>
              item._id !== this.importId &&
              item.sourceFormat &&
              myFormat &&
              item.sourceFormat !== myFormat
          );
        },
        error: () => {
          this.pairSearchResults = [];
        },
      });
  }

  confirmPair(other: InvoiceImportSummary): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.PHASE2.PAIR_CONFIRM_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.PHASE2.PAIR_CONFIRM_TEXT', {
        number: other.invoiceNumber,
        format: other.sourceFormat,
      }),
      icon: 'question',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.invoiceImportService.pairImport(this.storeId, this.importId, other._id).subscribe({
        next: () => {
          this.spinner.hide();
          this.pairPickerOpen = false;
          this.loadDetail();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.showApiError(err);
        },
      });
    });
  }

  confirmUnpair(): void {
    Swal.fire({
      title: this.translate.instant('INVOICE_IMPORTS.PHASE2.UNPAIR_TITLE'),
      text: this.translate.instant('INVOICE_IMPORTS.PHASE2.UNPAIR_TEXT'),
      icon: 'warning',
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.invoiceImportService.unpairImport(this.storeId, this.importId).subscribe({
        next: () => {
          this.spinner.hide();
          this.loadDetail();
        },
        error: (err: any) => {
          this.spinner.hide();
          this.showApiError(err);
        },
      });
    });
  }

  depositDisplayName(line: InvoiceImportLine): string {
    if (line.productSnapshot?.name) {
      return line.productSnapshot.name;
    }
    return this.translate.instant('INVOICE_IMPORTS.PHASE2.PANT_NOT_MAPPED');
  }

  private showApiError(err: any): void {
    const code = apiErrorCode(err);
    const msg = err.error?.response?.message || err.message;
    Swal.fire('Error', code ? `${msg} (${code})` : msg, 'error');
  }

  startProductSearch(line: InvoiceImportLine): void {
    this.productSearchLineId = line._id;
    this.productSearchQ = line.ean || line.name || '';
    this.searchProducts();
  }

  searchProducts(): void {
    if (!this.productSearchQ || this.productSearchQ.length < 2) {
      this.productSearchResults = [];
      return;
    }
    this.ecommerceService
      .getStoreProducts(this.storeId, { page: 1, limit: 10, search: this.productSearchQ, adminView: true })
      .subscribe({
        next: (res: any) => {
          const data = res.response?.data || res.data || {};
          this.productSearchResults = data.products || [];
        },
        error: () => {
          this.productSearchResults = [];
        },
      });
  }

  linkProduct(line: InvoiceImportLine, productId: string): void {
    const draft = this.lineDrafts[line._id];
    const body: Record<string, unknown> = { productId };
    if (draft?.newSalePrice != null) {
      body['newSalePrice'] = draft.newSalePrice;
    }
    this.spinner.show();
    this.invoiceImportService.patchLine(this.storeId, this.importId, line._id, body).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        this.productSearchLineId = null;
        this.applyLineResponse(res);
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  private applyLineResponse(res: any): void {
    const data = res.response?.data || res.data || {};
    if (data.line) {
      const idx = this.lines.findIndex((l) => l._id === data.line._id);
      if (idx >= 0) {
        this.lines[idx] = data.line;
      } else {
        this.lines.push(data.line);
      }
    }
    if (data.lineCounts && this.imp) {
      this.imp = { ...this.imp, lineCounts: data.lineCounts };
    }
    if (data.reviewSummary) {
      this.reviewSummary = data.reviewSummary;
    }
    if (data.line) {
      this.syncDraftForLine(data.line);
    }
  }

  private syncDraftForLine(line: InvoiceImportLine): void {
    this.lineDrafts[line._id] = {
      acceptCostPrice: line.acceptCostPrice !== false,
      newSalePrice: line.newSalePrice ?? null,
      costKeepReason: line.costKeepReason || '',
    };
  }

  effectiveTotalUnits(line: InvoiceImportLine): number {
    if (line.totalUnits != null && !Number.isNaN(line.totalUnits)) {
      return line.totalUnits;
    }
    if (line.orderedPacks != null && line.quantity != null) {
      return line.orderedPacks * line.quantity;
    }
    return line.quantity ?? 0;
  }

  quantityBreakdownLabel(line: InvoiceImportLine): string {
    const unit = line.unit || '';
    if (line.orderedPacks != null && line.orderedPacks !== undefined) {
      return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.QUANTITY_BREAKDOWN', {
        packs: line.orderedPacks,
        quantity: line.quantity,
        unit,
        total: this.effectiveTotalUnits(line),
      });
    }
    return translateInstant(this.translate, 'INVOICE_IMPORTS.DETAIL.QUANTITY_SIMPLE', {
      quantity: line.quantity,
      unit,
    });
  }

  lineProductName(line: InvoiceImportLine): string {
    return line.productSnapshot?.name || line.name || '—';
  }

  lineOrderNo(line: InvoiceImportLine): string | null {
    const raw = line.supplierItemNumber || line.orderReference;
    if (raw == null) {
      return null;
    }
    const trimmed = String(raw).trim();
    return trimmed.length ? trimmed : null;
  }

  lineImageUrl(line: InvoiceImportLine): string | null {
    return line.productSnapshot?.imageUrl || null;
  }

  documentTypeLabel(): string {
    if (!this.imp) {
      return '';
    }
    if (this.imp.documentSubtype === 'pass_through') {
      return this.translate.instant('INVOICE_IMPORTS.DETAIL.DOC_PASS_THROUGH');
    }
    if (this.imp.documentType === 'credit_note') {
      return this.translate.instant('INVOICE_IMPORTS.DETAIL.DOC_CREDIT_NOTE');
    }
    return this.imp.documentType || '';
  }

  trackByLineId(_index: number, line: InvoiceImportLine): string {
    return line._id;
  }

  trackByDisplayRow(_index: number, row: InvoiceImportDisplayRow): string {
    return row.line._id;
  }

  isMarginBelowMinimum(line: InvoiceImportLine): boolean {
    const min = this.thresholds?.minimumMarginPercent;
    if (min == null || line.marginPercentAtCurrentPrice == null) {
      return false;
    }
    return line.marginPercentAtCurrentPrice < min;
  }
}
